import React, { SetStateAction, useEffect, useState } from "react";
import styles from "./BillsCard.module.css";

//Icons
import PendingActionsIcon from "@mui/icons-material/PendingActions";

import { AxiosResponse } from "axios";
import { useUser } from "../../Contexts/UserContext/UserContext";
import { IBillListDTO, parseBillEmailStatus } from "../../Interfaces/Bill";

import { Paid, Schedule } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useBills } from "../../Contexts/BillContext/BillProvider";
import {
  IPerson,
  useContacts,
} from "../../Contexts/ContactsContext/ContactsProvider";
import { useModals } from "../../Contexts/DialogContext/DialogProvider";
import useAuthConnection from "../../Hooks/useAuthConnection";
import MoreOptionsButton from "../Buttons/MoreOptionsButton/MoreOptionsButton";
import { useMobileView } from "../../Hooks/useMobileView";
import dayjs from "dayjs";
import { Tooltip, useMediaQuery } from "@mui/material";

interface props {
  billDTO: IBillListDTO;
  setClickedBill?: React.Dispatch<SetStateAction<IBillListDTO | undefined>>;
}

interface Document {
  id: number;
  title: string;
  description: string;
  fileEnding: string;
  fileId: string;
  base64: string;
  organizationId: number;
}

const BillRow = ({ billDTO, setClickedBill }: props) => {
  let navigate = useNavigate();
  const connection = useAuthConnection();
  const { changeVisibility } = useModals();
  const billContext = useBills();
  const isMobile = useMediaQuery("(max-width: 1300px)");

  const [expanded, setExpanded] = useState<boolean>(false);
  const { user } = useUser();

  let { bill } = billDTO;

  useEffect(() => {
    bill = billDTO.bill;
  }, [billDTO]);

  const onPressEdit = () => {
    if (billDTO.bill.isReceipt === false) {
      navigate(`bearbeiten/${bill.billPrefix}-${bill.billNumber}`);
    } else {
      navigate(
        `/rechnungswesen/belege/bearbeiten/${bill.billPrefix}-${bill.billNumber}`,
        { replace: true }
      );
    }
  };

  const onPressDelete = () => {
    changeVisibility!("yesNoDialog", true, {
      props: {
        object: {
          submitFunction: () => {
            billContext.deleteBill!(bill);
            connection
              .delete("/bill", { data: bill })
              .then((res: AxiosResponse) => {});
          },
          modalTitle: "Rechnung löschen",
          confirmButtonText: "Löschen",
          modalText: "Möchten Sie diese Rechnung wirklich löschen?",
        },
      },
    });
    /*billContext.deleteBill!(bill);
        connection.delete("/bill", { data: bill })
            .then((res: AxiosResponse) => {
            });*/
  };

  const options = [
    {
      text: "Bearbeiten",
      function: onPressEdit,
    },
    {
      text: "Löschen",
      function: onPressDelete,
    },
  ];

  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  }

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const { persons } = useContacts();

  const [paymentIsLate, setPaymentIsLate] = useState(false);

  useEffect(() => {
    if (!bill.isPaid) {
      // Get the current date
      const currentDate = new Date();

      // Get the date you want to compare (e.g. from a database or user input)
      const dateToCompare = new Date(bill.date ?? ""); // Replace this with the date you want to compare

      // Calculate the time difference between the current date and the date to compare
      const timeDifference = currentDate.getTime() - dateToCompare.getTime();

      // Convert the time difference from milliseconds to days
      const daysDifference = timeDifference / (1000 * 60 * 60 * 24);

      if (daysDifference > 30) {
        setPaymentIsLate(true);
      }
    }
  }, [bill.isPaid]);

  const getCustomerDisplayName = (bill: any) => {
    if (bill.customerName) {
      return (
        <span
          style={{
            fontSize: "1em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            msTextOverflow: "ellipsis",
          }}
        >
          {bill.customerName.substring(0, 25)}
        </span>
      );
    }

    const person = persons.find((temp: IPerson) => temp.id === bill.personId);
    if (person) {
      return "Person: " + person.completeName?.substring(0, 25);
    }

    return "Kein Kunde zugewiesen";
  };

  return (
    <>
      {!isMobile && bill && (
        <div
          className="flex flex-row w-full hover:bg-slate-50 items-center justify-between min-h-[50px]"
          onClick={() => {
            if (setClickedBill) setClickedBill(billDTO);
          }}
        >
          <div className="w-[5%] flex justify-center items-center">
            {!bill.isCreated && (
              <Tooltip title="Entwurf">
                <PendingActionsIcon style={{ color: "#5e5e5eee" }} />
              </Tooltip>
            )}
            {bill.isCreated && (
              <Tooltip
                title={
                  bill.isPaid
                    ? "Bezahlt"
                    : paymentIsLate
                    ? "Im Verzug"
                    : "Offen"
                }
              >
                <Paid
                  style={{
                    color: bill.isPaid
                      ? "#105a37"
                      : paymentIsLate
                      ? "#cc3131"
                      : "#5e5e5eee",
                  }}
                />
              </Tooltip>
            )}
          </div>
          <div className="w-[15%]">
            <span>
              {bill.billPrefix}-{bill.billNumber}
            </span>
          </div>
          <div className="w-[35%]">
            <span>{getCustomerDisplayName(bill)}</span>
          </div>
          <div className="w-[15%]">
            <span>
              {bill.date ? dayjs(bill.date).format("DD.MM.YYYY") : ""}
            </span>
          </div>
          <div className="w-[15%]">
            <span>{bill?.netto?.toFixed(2).replace(".", ",")} €</span>
          </div>
          <MoreOptionsButton options={options} />
        </div>
      )}

      {isMobile && bill && (
        <div
          className="flex flex-row w-full p-2 border-b-2 items-center"
          onClick={() => {
            if (setClickedBill) setClickedBill(billDTO);
          }}
        >
          <div className="flex flex-col w-[90%]">
            <div className="flex flex-row justify-between">
              <span>Kunde:</span>
              <span>{bill.customerName}</span>
            </div>

            <div className="flex flex-row justify-between">
              <span>Rechnungsnr.:</span>
              <span>
                {bill.billPrefix}-{bill.billNumber}
              </span>
            </div>

            <div className="flex flex-row justify-between">
              <span>Datum:</span>
              <span>
                {bill.date ? dayjs(bill.date).format("DD.MM.YYYY") : ""}
              </span>
            </div>

            <div className="flex flex-row justify-between">
              <span>Status:</span>
              <span>
                {!bill.isCreated && (
                  <Tooltip title="Entwurf">
                    <PendingActionsIcon style={{ color: "#5e5e5eee" }} />
                  </Tooltip>
                )}
                {bill.isCreated && (
                  <Tooltip
                    title={
                      bill.isPaid
                        ? "Bezahlt"
                        : paymentIsLate
                        ? "Im Verzug"
                        : "Offen"
                    }
                  >
                    <Paid
                      style={{
                        color: bill.isPaid
                          ? "#105a37"
                          : paymentIsLate
                          ? "#cc3131"
                          : "#5e5e5eee",
                      }}
                    />
                  </Tooltip>
                )}
              </span>
            </div>

            <div className="flex flex-row justify-between">
              <span>Betrag:</span>
              <span>{bill?.netto?.toFixed(2).replace(".", ",")} €</span>
            </div>

            <div className="flex flex-row justify-between">
              <span>Typ:</span>
              <span>
                {bill.isReceipt
                  ? "Beleg"
                  : bill.subscriptionId
                  ? "Intervallrechnung"
                  : "Rechnung"}
              </span>
            </div>
          </div>
          <div className="flex flex-col w-[10%] mr-3">
            <MoreOptionsButton options={options} />
          </div>
        </div>
      )}
    </>
  );
};

export default BillRow;
